import type {
  RumInitConfiguration,
  RumEvent,
  RumEventDomainContext,
} from "@datadog/browser-rum";

type DatadogPrivacyLevel = "mask-user-input" | "mask" | "allow";
type BeforeSendCallback = (
  event: RumEvent,
  context: RumEventDomainContext
) => boolean;

/**
 * Data Transfer Object (DTO) for Datadog configuration.
 * Represents the parameters for initializing Datadog RUM (Real User Monitoring).
 */
export class DatadogConfigDTO implements RumInitConfiguration {
  allowedTracingUrls?: string[];
  applicationId: string;
  beforeSend?: BeforeSendCallback;
  clientToken: string;
  defaultPrivacyLevel?: DatadogPrivacyLevel;
  env?: string;
  service?: string;
  sessionReplaySampleRate?: number;
  sessionSampleRate?: number;
  site?: string;
  traceSampleRate?: number;
  trackLongTasks?: boolean;
  trackResources?: boolean;
  trackUserInteractions?: boolean;
  version?: string;

  /**
   * Constructs a new DatadogConfigDTO.
   *
   * @constructor
   * @param {string} params.applicationId - RUM application ID (required).
   * @param {string} params.clientToken - RUM client token (required).
   * @param {string[]} [params.allowedTracingUrls] - URLs to trace for distributed tracing.
   * @param {function} [params.beforeSend] - Callback to filter or modify events before they are sent.
   * @param {string} [params.defaultPrivacyLevel="mask-user-input"] - Privacy level for data collection.
   * @param {string} [params.env] - The environment name ("prd", "hml", "azl").
   * @param {string} [params.service] - Name of the monitored service.
   * @param {number} [params.sessionReplaySampleRate=10] - Percentage of sessions to capture replay (0-100).
   * @param {number} [params.sessionSampleRate=10] - Percentage of sessions to track (0-100).
   * @param {string} [params.site="datadoghq.com"] - Datadog site to send data to.
   * @param {number} [params.traceSampleRate=100] - Percentage of traces to capture (0-100).
   * @param {boolean} [params.trackLongTasks=true] - Whether to track long UI tasks.
   * @param {boolean} [params.trackResources=true] - Whether to track resource requests (e.g., API calls).
   * @param {boolean} [params.trackUserInteractions=true] - Whether to track user interactions.
   * @param {string} [params.version] - Version of the application being monitored.
   * @throws {Error} If applicationId or clientToken is missing.
   */
  constructor({
    applicationId,
    clientToken,
    allowedTracingUrls,
    defaultPrivacyLevel = "mask-user-input",
    env,
    service,
    sessionSampleRate = 10,
    sessionReplaySampleRate = 10,
    site = "datadoghq.com",
    traceSampleRate = 100,
    trackLongTasks = true,
    trackResources = true,
    trackUserInteractions = true,
    version,
  }: DatadogConfigDTO) {
    if (!applicationId) {
      throw new Error(
        "The 'applicationId' is required for Datadog RUM initialization."
      );
    }
    if (!clientToken) {
      throw new Error(
        "The 'clientToken' is required for Datadog RUM initialization."
      );
    }

    this.applicationId = applicationId;
    this.clientToken = clientToken;
    this.allowedTracingUrls = allowedTracingUrls;
    this.defaultPrivacyLevel = defaultPrivacyLevel;
    this.env = env;
    this.service = service;
    this.sessionSampleRate = sessionSampleRate;
    this.sessionReplaySampleRate = sessionReplaySampleRate;
    this.site = site;
    this.traceSampleRate = traceSampleRate;
    this.trackLongTasks = trackLongTasks;
    this.trackResources = trackResources;
    this.trackUserInteractions = trackUserInteractions;
    this.version = version;
  }
}
